// Select — the single-choice dropdown primitive, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// One value out of a list, chosen from a floating popover. Listbox
// semantics — typeahead, arrow-key nav, Home/End, Esc-to-dismiss, focus
// return to the trigger, a single tab stop, and the correct
// role="combobox"/listbox/option + aria-activedescendant wiring — are the
// hard-a11y contract we do NOT hand-roll; we wrap @radix-ui/react-select,
// which owns all of it. This file only styles and shapes the surface.
//
// Register (.claude/research/design/06): the trigger matches the Input register — a
// calm, borderless field carried by a translucent ink tint (NOT a resting
// border, NOT a fill on --color-bg-elevated which equals --color-input-bg on the
// white canvas and would vanish); the edge is revealed only by interaction
// (a focus ring on :focus-visible), the border→elevation move (§2/§5). The
// popover content is a Surface: it floats on --color-bg-elevated with a
// soft --shadow-md, so "this floats" is carried by elevation, not a painted
// edge. No accent is spent — a value picker is not "the primary action in
// this view", so it stays neutral (spend the accent elsewhere).
//
// Selection is marked by a checkmark on the chosen option — a SHAPE, not a
// colour — so which option is current survives SC 1.4.1 (colour is never
// the sole signal).
//
// Pure/props-driven: `value` + `onValueChange` make it fully controlled —
// no internal selection state, every change reported UP. `options` is the
// data; deps arrive as props; no I/O, no domain knowledge. `ariaLabel` is
// required because a combobox with no visible label is unlabelled to AT and
// there is no other place to name it. All user-facing text (option labels,
// the placeholder, the group label) is caller-owned — the primitive authors
// no string of its own, so it needs no Lingui macro here (the Segmented
// control precedent).

import * as SelectPrimitive from "@radix-ui/react-select";
import { Icon } from "../Icon/Icon.js";
import "./Select.css";

// One selectable option. `value` is the identity reported through
// onValueChange; `label` is the visible text.
export type SelectOption = {
    readonly value: string;
    readonly label: string;
};

export type SelectProps = {
    readonly options: readonly SelectOption[];
    readonly value: string;
    readonly onValueChange: (value: string) => void;
    // Shown on the trigger while no value is selected. Caller-owned text.
    readonly placeholder?: string;
    // Names the control for assistive tech (SC 4.1.2 / 1.3.1). Required.
    readonly ariaLabel: string;
    readonly disabled?: boolean;
    // A short label shown before the value on the trigger ("User:"), for
    // filter chips. The list items show the bare option labels.
    readonly prefix?: string;
};

// Decorative glyphs — the label/state is carried by role + text, so both
// are aria-hidden. The ONE registry forms (P33): chevron-down for the
// trigger, check for the selected-item indicator.
const chevron = <Icon name="chevron-down" />;
const check = <Icon name="check" />;

export const Select = ({
    options,
    value,
    onValueChange,
    placeholder,
    ariaLabel,
    disabled = false,
    prefix,
}: SelectProps) => (
    <SelectPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
    >
        <SelectPrimitive.Trigger
            className="bilda-select__trigger"
            aria-label={ariaLabel}
        >
            <span className="bilda-select__label">
                {prefix === undefined ? null : (
                    <span className="bilda-select__prefix">{prefix}</span>
                )}
                <SelectPrimitive.Value placeholder={placeholder} />
            </span>
            <SelectPrimitive.Icon className="bilda-select__chevron">
                {chevron}
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                className="bilda-select__content"
                position="popper"
                sideOffset={4}
            >
                <SelectPrimitive.Viewport className="bilda-select__viewport">
                    {options.map((option) => (
                        <SelectPrimitive.Item
                            key={option.value}
                            value={option.value}
                            className="bilda-select__item"
                        >
                            <SelectPrimitive.ItemText>
                                {option.label}
                            </SelectPrimitive.ItemText>
                            <SelectPrimitive.ItemIndicator className="bilda-select__check">
                                {check}
                            </SelectPrimitive.ItemIndicator>
                        </SelectPrimitive.Item>
                    ))}
                </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
);
